/**
 * Animated NFT Support Utilities
 * Handles detection, validation, and configuration for animated NFT projects
 */

import { getConfig } from '../lib/publicConfig';

// Supported output formats
export const OUTPUT_FORMATS = {
  STATIC: ['png', 'jpg', 'jpeg', 'webp'],
  ANIMATED: ['mp4', 'webm', 'gif', 'apng'],
};

// All supported formats
export const ALL_OUTPUT_FORMATS = [
  ...OUTPUT_FORMATS.STATIC,
  ...OUTPUT_FORMATS.ANIMATED,
];

// Supported input/layer formats
export const LAYER_FORMATS = {
  STATIC: ['png', 'jpg', 'jpeg', 'webp', 'svg'],
  ANIMATED: ['mp4', 'webm', 'gif', 'apng'],
};

// Features not supported in animated projects
// NOTE: Paths and Rules (excluding color-harmony and rule-templates) work with animated projects
export const UNSUPPORTED_ANIMATED_FEATURES = [
  'color-harmony',
  'rule-templates',
];

// Max duration for animated projects (seconds)
export const MAX_ANIMATED_DURATION = 15; // 15 seconds max

// Collection size limits
export const MAX_STATIC_COLLECTION_SIZE = 50000; // 50k NFTs max for static
export const MAX_ANIMATED_COLLECTION_SIZE = 10000; // 10k NFTs max for animated

// Feature display names for messaging
export const FEATURE_NAMES = {
  'color-harmony': 'Color Harmony Rules',
  'rule-templates': 'Rule Templates',
};

/**
 * Check if a project is animated based on output format
 * @param {string} outputFormat - The output format (png, mp4, etc.)
 * @returns {boolean}
 */
export const isAnimatedProject = (outputFormat) => {
  if (!outputFormat) return false;
  return OUTPUT_FORMATS.ANIMATED.includes(outputFormat.toLowerCase());
};

/**
 * Get maximum collection size based on project type
 * @param {string} outputFormat - The output format
 * @returns {number} Maximum allowed collection size
 */
export const getMaxCollectionSize = (outputFormat) => {
  return isAnimatedProject(outputFormat) 
    ? MAX_ANIMATED_COLLECTION_SIZE 
    : MAX_STATIC_COLLECTION_SIZE;
};

/**
 * Check if a format is animated
 * @param {string} format - The format to check
 * @returns {boolean}
 */
export const isAnimatedFormat = (format) => {
  return OUTPUT_FORMATS.ANIMATED.includes(format?.toLowerCase());
};

/**
 * Check if a format is static
 * @param {string} format - The format to check
 * @returns {boolean}
 */
export const isStaticFormat = (format) => {
  return OUTPUT_FORMATS.STATIC.includes(format?.toLowerCase());
};

/**
 * Check if a layer format is animated
 * @param {string} filename - Filename with extension
 * @returns {boolean}
 */
export const isAnimatedLayer = (filename) => {
  if (!filename) return false;
  const ext = filename.split('.').pop()?.toLowerCase();
  return LAYER_FORMATS.ANIMATED.includes(ext);
};

/**
 * Check if a feature is supported for the project type
 * @param {string} featurePath - Feature path (e.g., 'color-harmony')
 * @param {string} outputFormat - Project output format
 * @param {boolean} allowMixedOutput - Whether mixed collections are enabled
 * @returns {boolean}
 */
export const isFeatureSupported = (featurePath, outputFormat, allowMixedOutput = false) => {
  // Static projects: all features supported
  if (!isAnimatedProject(outputFormat)) return true;
  
  // Mixed collections: Color Harmony and Rule Templates work on image selection (not rendering)
  // They apply to all NFTs regardless of final output format
  if (allowMixedOutput && ['color-harmony', 'rule-templates'].includes(featurePath)) {
    return true;
  }
  
  // Fully animated projects: block color harmony and rule templates
  return !UNSUPPORTED_ANIMATED_FEATURES.includes(featurePath);
};

/**
 * Get feature restriction message for animated projects
 * @param {string} featurePath - Feature path
 * @returns {string}
 */
export const getFeatureRestrictionMessage = (featurePath) => {
  const featureName = FEATURE_NAMES[featurePath] || 'This feature';
  return `${featureName} is not supported for animated NFT projects. This feature is designed for static image manipulation and cannot be applied to video-based NFTs.`;
};

/**
 * Get the fixed price cost for animated NFTs in USD
 * @returns {number}
 */
export const getAnimatedPriceCost = () => {
  return getConfig('pricing.animated_cost', 2.00);
};

/**
 * Calculate price for animated NFT (fixed cost, not multiplier)
 * @returns {number} - Fixed price per animated NFT in USD
 */
export const calculateAnimatedPrice = () => {
  return getAnimatedPriceCost();
};

/**
 * Get file extension for output format
 * @param {string} outputFormat
 * @returns {string}
 */
export const getFileExtension = (outputFormat) => {
  return outputFormat?.toLowerCase() || 'png';
};

/**
 * Get MIME type for output format
 * @param {string} outputFormat
 * @returns {string}
 */
export const getMimeType = (outputFormat) => {
  const mimeTypes = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    webp: 'image/webp',
    gif: 'image/gif',
    apng: 'image/apng',
    mp4: 'video/mp4',
    webm: 'video/webm',
  };
  return mimeTypes[outputFormat?.toLowerCase()] || 'image/png';
};

/**
 * Get human-readable format name
 * @param {string} outputFormat
 * @returns {string}
 */
export const getFormatDisplayName = (outputFormat) => {
  const displayNames = {
    png: 'PNG (Static)',
    jpg: 'JPEG (Static)',
    jpeg: 'JPEG (Static)',
    webp: 'WebP (Static)',
    gif: 'GIF (Animated)',
    apng: 'APNG (Animated PNG)',
    mp4: 'MP4 (Animated)',
    webm: 'WebM (Animated)',
  };
  return displayNames[outputFormat?.toLowerCase()] || outputFormat?.toUpperCase();
};

/**
 * Validate animated project settings
 * @param {Object} settings - Project settings
 * @returns {Object} { valid: boolean, errors: string[] }
 */
export const validateAnimatedSettings = (settings) => {
  const errors = [];
  
  // Validate collection size based on project type
  const maxSize = getMaxCollectionSize(settings.output_format);
  if (settings.collection_size && settings.collection_size > maxSize) {
    const projectType = isAnimatedProject(settings.output_format) ? 'animated' : 'static';
    errors.push(`Collection size cannot exceed ${maxSize.toLocaleString()} for ${projectType} projects`);
  }
  
  if (isAnimatedProject(settings.output_format)) {
    // Validate FPS
    if (!settings.output_fps || settings.output_fps < 1 || settings.output_fps > 60) {
      errors.push('FPS must be between 1 and 60');
    }
    
    // Validate duration - enforce MAX_ANIMATED_DURATION
    if (!settings.output_duration || settings.output_duration < 0.5) {
      errors.push('Duration must be at least 0.5 seconds');
    } else if (settings.output_duration > MAX_ANIMATED_DURATION) {
      errors.push(`Duration cannot exceed ${MAX_ANIMATED_DURATION} seconds for animated projects`);
    }
  }
  
  // Validate quality
  if (settings.output_quality && (settings.output_quality < 1 || settings.output_quality > 100)) {
    errors.push('Quality must be between 1 and 100');
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Get default settings for project type
 * @param {string} outputFormat
 * @returns {Object}
 */
export const getDefaultSettings = (outputFormat) => {
  const isAnimated = isAnimatedProject(outputFormat);
  
  return {
    output_format: outputFormat || 'png',
    output_quality: outputFormat === 'png' ? 100 : 90,
    output_fps: isAnimated ? 30 : null,
    output_duration: isAnimated ? 3.0 : null,
  };
};

export default {
  isAnimatedProject,
  isAnimatedFormat,
  isStaticFormat,
  isAnimatedLayer,
  isFeatureSupported,
  getFeatureRestrictionMessage,
  getAnimatedPriceCost,
  calculateAnimatedPrice,
  getFileExtension,
  getMimeType,
  getFormatDisplayName,
  validateAnimatedSettings,
  getDefaultSettings,
  OUTPUT_FORMATS,
  LAYER_FORMATS,
  UNSUPPORTED_ANIMATED_FEATURES,
};
